import React from "react";
import Tooltip from '@mui/material/Tooltip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import styled from "@emotion/styled";
import { useTheme } from "@mui/styles";

const SelectedMemberStats = ({selectedMemberInfoObj, selectedMemberFirstName}) => {
  const theme = useTheme()

  const tooltipSx = {
    color: theme.palette.common.black,
    backgroundColor: theme.palette.common.white,
    border: `0.1rem solid ${theme.palette.common.grayBorder}`,
    boxShadow: '0 0.7rem 1.7rem rgba(15, 23, 42, 0.14)',
    borderRadius: '0.65rem',
    maxWidth: '17rem',
    padding: '0.75rem 0.85rem',
    fontSize: '0.78rem',
    fontWeight: 650,
    lineHeight: 1.4,
  }

  const formatNumber = (value) => value ? value.toLocaleString("en-US", {maximumFractionDigits: 0, minimumFractionDigits: 0}) : value

  const stats = [
    {
      label: "Renegotiations",
      value: selectedMemberInfoObj.closedRenegotiations,
      detail: "Closed",
    },
    {
      label: "Renegotiation Frequency",
      value: selectedMemberInfoObj.renegotiationFrequency,
      unit: "days",
      detail: "Average interval",
      tooltip: "Average days between this member closing a renegotiation.",
    },
    {
      label: "Refinances",
      value: selectedMemberInfoObj.closedRefinances,
      detail: "Closed",
    },
    {
      label: "Refinance Frequency",
      value: selectedMemberInfoObj.refinanceFrequency,
      unit: "days",
      detail: "Average interval",
      tooltip: "Average days between this member closing a refinance.",
    },
    {
      label: "Gross Profit",
      value: formatNumber(selectedMemberInfoObj.grossProfitNumber),
      prefix: "$",
      detail: "Refinance value",
      tooltip: "Value in parentheses is this member's average profit generated by all refinances.",
    },
    {
      label: "Gross Revenue Increase",
      value: formatNumber(selectedMemberInfoObj.grossProfitPercent),
      suffix: "%",
      detail: "Refinance lift",
      tooltip: "Value in parentheses is this member's average revenue increase generated by all refinances.",
    },
  ]

  return (
    <StatsSurface>
      <StatsHeader>
        <div>
          <SectionEyebrow>Closure Performance</SectionEyebrow>
          <SectionTitle>{selectedMemberFirstName}'s Closure Stats</SectionTitle>
        </div>
        <SectionSummary>Closed outcomes, average cadence, and refinance contribution.</SectionSummary>
      </StatsHeader>
      <StatsGrid>
        {stats.map((stat) => (
          <StatCard key={stat.label}>
            <StatLabelRow>
              <StatLabel>{stat.label}</StatLabel>
              {stat.tooltip &&
                <Tooltip
                  componentsProps={{tooltip: {sx: tooltipSx}}}
                  PopperProps={{modifiers: [{
                    name: "offset",
                    options: {
                      offset: [0, -6],
                    },
                  }]}}
                  title={stat.tooltip}
                  placement="top"
                  arrow
                >
                  <HelpButton aria-label={`${stat.label} context`} type="button">
                    <HelpOutlineIcon />
                  </HelpButton>
                </Tooltip>
              }
            </StatLabelRow>
            <StatValue>
              {stat.prefix && <Accent>{stat.prefix}</Accent>}
              {stat.value}
              {stat.suffix && <Accent>{stat.suffix}</Accent>}
              {stat.unit && <Unit>{stat.unit}</Unit>}
            </StatValue>
            <StatDetail>{stat.detail}</StatDetail>
          </StatCard>
        ))}
      </StatsGrid>
    </StatsSurface>
  )
}

const StatsSurface = styled.section(({ theme }) => `
  min-width: 0;
  display: grid;
  gap: 0;
  overflow: hidden;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.8rem;
  background: ${theme.palette.common.white};
  box-shadow: 0 0.25rem 0.9rem rgba(15, 23, 42, 0.05);
`)

const StatsHeader = styled.header(({ theme }) => `
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 1rem;
  align-items: end;
  padding: 0.95rem 1.05rem;
  border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
  background: ${theme.palette.common.grayBorderLight};
`)

const SectionEyebrow = styled.div(({ theme }) => `
  margin-bottom: 0.18rem;
  color: ${theme.palette.primary.main};
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.04rem;
  line-height: 1.2;
  text-transform: uppercase;
`)

const SectionTitle = styled.h2(({ theme }) => `
  margin: 0;
  color: ${theme.palette.common.black};
  font-size: 1.08rem;
  font-weight: 850;
  line-height: 1.2;
`)

const SectionSummary = styled.p(({ theme }) => `
  max-width: 24rem;
  margin: 0;
  color: ${theme.palette.common.grayText};
  font-size: 0.82rem;
  font-weight: 650;
  line-height: 1.35;
  text-align: right;
`)

const StatsGrid = styled.div(`
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 0.75rem;
  padding: 0.9rem 1rem 1rem;
`)

const StatCard = styled.article(({ theme }) => `
  min-width: 0;
  min-height: 7rem;
  display: grid;
  align-content: space-between;
  gap: 0.6rem;
  padding: 0.8rem;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.75rem;
  background: ${theme.palette.common.cardBackground};
`)

const StatLabelRow = styled.div(`
  min-width: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.45rem;
`)

const StatLabel = styled.span(({ theme }) => `
  min-width: 0;
  color: ${theme.palette.common.grayText};
  font-size: 0.78rem;
  font-weight: 800;
  line-height: 1.25;
`)

const HelpButton = styled.button(({ theme }) => `
  width: 1.35rem;
  height: 1.35rem;
  min-width: 1.35rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 999rem;
  background: transparent;
  color: ${theme.palette.common.grayText};
  cursor: help;

  svg {
    width: 1rem;
    height: 1rem;
  }

  &:hover {
    color: ${theme.palette.primary.dark};
    background: ${theme.palette.primary.labelTransparent};
  }
`)

const StatValue = styled.div(({ theme }) => `
  min-width: 0;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.2rem;
  color: ${theme.palette.common.black};
  font-size: 1.35rem;
  font-weight: 850;
  line-height: 1.05;
`)

const Accent = styled.span(({ theme }) => `
  color: ${theme.palette.primary.pastelMedium};
  font-size: 1rem;
  font-weight: 850;
`)

const Unit = styled.span(({ theme }) => `
  margin-left: 0.1rem;
  color: ${theme.palette.common.grayText};
  font-size: 0.78rem;
  font-weight: 750;
`)

const StatDetail = styled.div(({ theme }) => `
  color: ${theme.palette.common.grayText};
  font-size: 0.76rem;
  font-weight: 700;
  line-height: 1.2;
`)

export default SelectedMemberStats
